import React, { SetStateAction, useEffect, useRef, useState } from 'react';
import {
    Box,
    Flex,
    Text,
    useDisclosure,
    Icon,
} from '@chakra-ui/react';
import { useAntecipation } from '@/hooks/useAntecipation';
import { showToast } from '@/utils';
import { AiOutlineInfoCircle } from 'react-icons/ai';
import { TbCalendarDollar } from "react-icons/tb";
import { Tooltip, Input, Button, Drawer, Pills } from "barte-design-system";



interface SellerRateSimulator {
    netAmount?: number;
    grossAmount?: number;
    anticipationDate?: string;
    effectiveRate?: any;
}

interface antecipation {
    data: any;
    setUpdatedRedeens: React.Dispatch<SetStateAction<number>>
    updatedRedeens?: number
}

export default function ModalAntecipacao({ data, updatedRedeens, setUpdatedRedeens }: antecipation) {
    const { isOpen, onOpen, onClose } = useDisclosure();
    const { simulateAnticipation, confirmAnticipation, loading } = useAntecipation()

    const [inputValue, setInputValue] = useState('');
    const [inputValueNumber, setInputValueNumber] = useState<Number>(0);
    const [isLoading, setIsLoading] = useState(false);
    const inputWrapperRef = useRef<HTMLDivElement>(null);
    const [valueSimulation, setValueSimulation] = React.useState<SellerRateSimulator>({
        netAmount: 0,
        grossAmount: 0,
        anticipationDate: '',
        effectiveRate: ''
    });
    const formatDate = (dateStr?: string) => {
        if (!dateStr) {
            const now = new Date();
            const day = String(now.getDate()).padStart(2, '0');
            const month = String(now.getMonth() + 1).padStart(2, '0');
            const year = now.getFullYear();
            return `${day}/${month}/${year}`;
        }
        const [year, month, day] = dateStr.split('-').map(Number);
        const localDate = new Date(year, month - 1, day);
        const dd = String(localDate.getDate()).padStart(2, '0');
        const mm = String(localDate.getMonth() + 1).padStart(2, '0');
        const yyyy = localDate.getFullYear();
        return `${dd}/${mm}/${yyyy}`;
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        let raw = e.target.value.replace(/\D/g, '');
        setInputValueNumber(Number(raw) / 100);
        if (!raw) {
            setInputValue('');
            setValueSimulation(null);
            return;
        }

        const value = Number(raw) / 100
        setInputValue(value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }));
        setValueSimulation(null);
    };

    const handleSimulationOrConfirm = async () => {
        if (!valueSimulation) {
            setIsLoading(true);
            let raw = inputValue.replace(/\D/g, '');

            function formatValue(value) {
                const valueFormated = value?.replace("R$", "")?.replaceAll(".", "")?.replaceAll(",", "") / 100
                return valueFormated;
            }
            const value = Number(raw) / 100

            if (value >= 0 && value <= formatValue(data?.onDemandNetAmount)) {
                try {
                    const response = await simulateAnticipation(value);
                    setValueSimulation(response);
                } catch (err) {
                    console.error("Erro na simulação:", err);
                } finally {
                    setIsLoading(false);
                }
            } else {
                try {
                    const response = await simulateAnticipation(formatValue(data?.onDemandNetAmount));
                    setValueSimulation(response);
                    setInputValue(data?.onDemandNetAmount.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }));
                } catch (err) {
                    console.error("Erro na simulação:", err);
                } finally {
                    setIsLoading(false);
                }
            }
        } else {
            const data = {
                amount: Number(inputValueNumber)
            }
            try {
                const response = await confirmAnticipation(data)
                if (response) {
                    showToast({
                        type: 'success',
                        message: "Saldo antecipado com sucesso",
                    });
                }
            } catch (error) {
                return error
            } finally {
                onClose()
                setUpdatedRedeens(updatedRedeens + 1)
            }
        }
    }

    useEffect(() => {
        if (!isOpen) {
            setInputValue('');
            setValueSimulation(null);
            return;
        }
        const timer = setTimeout(() => {
            inputWrapperRef.current?.querySelector<HTMLInputElement>('input')?.focus();
        }, 150);
        return () => clearTimeout(timer);
    }, [isOpen])

    return (
        <>
            <Tooltip isDisabled={data?.isOnDemandEnabled === true} label="Solicite ao suporte a atualização de cadastro" placement="top">
                <Box>
                    <Button
                        variant="outline"
                        onClick={onOpen}
                        disabled={data?.isOnDemandEnabled === false || data?.onDemandNetAmount === 0}
                        size="base"
                        round="full"
                        iconLeft={<Icon as={TbCalendarDollar} boxSize="16px" />}
                    >
                        Antecipar
                    </Button>
                </Box>
            </Tooltip>

            <Drawer
                isOpen={isOpen}
                title="Antecipação de recebíveis"
                onClose={onClose}
                footer={{
                    primaryButton: {
                        children: valueSimulation ? "Confirmar antecipação" : "Simular antecipação",
                        onClick: handleSimulationOrConfirm,
                        loading: loading as boolean,
                        disabled: !inputValue || loading === true,
                    },
                    secondaryButton: {
                        children: "Cancelar",
                        onClick: onClose,
                        variant: "outline",
                    },
                }}
            >
                <Flex flexDirection="column" gap="16px" color="var(--content-primary)">
                    <Text fontSize="sm" color="var(--content-secondary)">
                        Antecipe seus recebíveis futuros e receba o valor líquido na sua conta.
                    </Text>

                    <Flex
                        flexDirection="column"
                        border="1px solid var(--stroke-primary)"
                        borderRadius="8px"
                        px="16px"
                    >
                        <Flex justify="space-between" align="center" py="16px">
                            <Text fontSize="sm">Disponível para antecipação</Text>
                            <Pills label={data?.onDemandNetAmount} variant="light" state="accent" size="sm" />
                        </Flex>
                    </Flex>

                    <Flex
                        ref={inputWrapperRef}
                        flexDirection="column"
                        gap="12px"
                        border="1px solid var(--stroke-primary)"
                        borderRadius="8px"
                        p="16px"
                    >
                        <Text fontSize="sm" fontWeight="600">Qual o valor você quer antecipar?</Text>
                        <Input
                            name="amount"
                            placeholder="Digite o valor"
                            value={inputValue}
                            onChange={(e) => handleChange(e as React.ChangeEvent<HTMLInputElement>)}
                        />
                    </Flex>

                    {isLoading ? (
                        <Flex
                            border="1px solid var(--stroke-primary)"
                            borderRadius="8px"
                            p="16px"
                            minH="120px"
                            justify="center"
                            align="center"
                        >
                            <Text fontSize="sm">Carregando...</Text>
                        </Flex>
                    ) : valueSimulation ? (
                        <Flex
                            flexDirection="column"
                            gap="12px"
                            border="1px solid var(--stroke-primary)"
                            borderRadius="8px"
                            p="16px"
                        >
                            <Text fontSize="sm" fontWeight="600">Dados para revisar</Text>
                            <Flex justify="space-between" align="center">
                                <Flex align="center" gap="4px">
                                    <Text fontSize="xs" color="var(--content-secondary)">Valor líquido</Text>
                                    <Tooltip placement="top" label="A antecipação é realizada com base nos seus recebíveis disponíveis. Caso o valor solicitado seja menor que o valor de um recebível, o sistema antecipará o recebível mais próximo, o que pode resultar em um valor maior sendo antecipado, acrescido das taxas aplicáveis.">
                                        <span><Icon w=".8rem" color="gray.400" as={AiOutlineInfoCircle} /></span>
                                    </Tooltip>
                                </Flex>
                                <Text fontSize="sm" fontWeight="600">{valueSimulation?.netAmount?.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}</Text>
                            </Flex>
                            <Flex justify="space-between">
                                <Text fontSize="xs" color="var(--content-secondary)">Valor bruto</Text>
                                <Text fontSize="xs" fontWeight="600">{valueSimulation?.grossAmount?.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}</Text>
                            </Flex>
                            <Flex justify="space-between">
                                <Text fontSize="xs" color="var(--content-secondary)">Período de antecipação</Text>
                                {valueSimulation?.anticipationDate && (
                                    <Text fontSize="xs" fontWeight="600">{formatDate()} a {formatDate(valueSimulation?.anticipationDate)}</Text>
                                )}
                            </Flex>
                            <Flex justify="space-between">
                                <Text fontSize="xs" color="var(--content-secondary)">Taxa efetiva</Text>
                                <Text fontSize="xs" fontWeight="600">{valueSimulation?.effectiveRate}%</Text>
                            </Flex>
                        </Flex>
                    ) : null}
                </Flex>
            </Drawer>
        </>
    );
}
